import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CopyrightProtection, { CopyrightPolicy } from '../components/CopyrightProtection';

const CopyrightPage: React.FC = () => {
  const copyrightPolicies: CopyrightPolicy[] = [
    {
      id: 1,
      title: 'Utilizare Educațională',
      description: 'Reguli pentru utilizarea resurselor în scop educațional',
      details: 'Materialele din biblioteca noastră pot fi utilizate pentru studiu și cercetare, cu condiția citării corespunzătoare a surselor. Este permisă descărcarea, imprimarea și distribuirea în scopuri educaționale non-comerciale.'
    },
    {
      id: 2,
      title: 'Materiale în Domeniul Public',
      description: 'Informații despre operele din domeniul public',
      details: 'Operele din domeniul public nu mai sunt protejate de drepturi de autor și pot fi utilizate liber în orice scop. Biblioteca noastră digitală identifică clar aceste materiale pentru a facilita utilizarea lor nerestricționată.'
    },
    {
      id: 3,
      title: 'Utilizare Personală',
      description: 'Condiții pentru utilizarea personală a materialelor',
      details: 'Utilizatorii pot descărca și salva conținut pentru uz personal, fără dreptul de a distribui sau republica materialele protejate de drepturi de autor. Această utilizare trebuie să rămână strict personală și non-comercială.'
    },
    {
      id: 4,
      title: 'Materiale Digitizate',
      description: 'Reguli pentru materialele digitizate din colecția noastră',
      details: 'Materialele digitizate de biblioteca noastră respectă acordurile cu deținătorii de drepturi și legislația în vigoare. Acestea pot avea restricții specifice de utilizare, indicate în metadatele fiecărui document.'
    },
    {
      id: 5,
      title: 'Excepții și Limitări',
      description: 'Excepții legale de la protecția drepturilor de autor',
      details: 'Anumite utilizări pot fi permise fără autorizare în baza excepțiilor și limitărilor prevăzute de lege, cum ar fi citarea, parodia, critica sau utilizarea în scopuri de predare. Verificați legislația locală pentru detalii.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow">
        {/* Hero section */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 py-12">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center">
              <div className="mb-8 md:mb-0 md:mr-8">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Protecția Drepturilor de Autor</h1>
                <p className="text-gray-600 text-lg max-w-3xl">
                  Biblioteca noastră digitală respectă și protejează drepturile de autor pentru toate materialele disponibile. 
                  Aici veți găsi informații despre politicile noastre de copyright și modul în care puteți utiliza resursele în mod legal.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 py-4 border-b">
          <div className="flex items-center text-sm text-gray-500">
            <Link to="/" className="hover:text-blue-600 transition">Acasă</Link>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-gray-700 font-medium">Protecția Drepturilor de Autor</span>
          </div>
        </div>
        
        {/* Main content */}
        <CopyrightProtection policies={copyrightPolicies} />
        
        {/* FAQ section */}
        <div className="bg-gray-50 py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">Întrebări Frecvente despre Drepturi de Autor</h2>
            
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Ce înseamnă "domeniul public"?
                </h3>
                <p className="text-gray-600">
                  Operele din domeniul public sunt cele pentru care protecția drepturilor de autor a expirat sau care nu au fost niciodată protejate.
                  Acestea aparțin publicului și pot fi utilizate liber, fără restricții. În general, în România, operele intră în domeniul public la 70 de ani
                  după moartea autorului.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Ce este o licență Creative Commons?
                </h3>
                <p className="text-gray-600">
                  Licențele Creative Commons sunt instrumente juridice standardizate care permit autorilor să acorde anumite drepturi publicului,
                  menținând în același timp controlul asupra operelor lor. Aceste licențe specifică condițiile în care operele pot fi utilizate, 
                  cum ar fi atribuirea autorului, utilizarea non-comercială sau crearea de opere derivate.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Pot folosi materialele pentru studiu personal?
                </h3>
                <p className="text-gray-600">
                  Da, puteți utiliza materialele din biblioteca noastră pentru studiu personal, indiferent de statutul lor de copyright.
                  Cu toate acestea, redistribuirea, copierea sau utilizarea comercială a materialelor protejate prin drepturi de autor
                  necesită permisiunea explicită a deținătorilor de drepturi, cu excepția cazurilor acoperite de excepțiile legale.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Cum pot raporta o încălcare a drepturilor de autor?
                </h3>
                <p className="text-gray-600">
                  Dacă observați o posibilă încălcare a drepturilor de autor pe platforma noastră, vă rugăm să o raportați prin email la
                  adresa copyright@biblioteca-digitala.ro sau utilizând formularul de contact. Includeți detalii despre materialul în cauză,
                  drepturile de autor presupus încălcate și orice alte informații relevante.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CopyrightPage; 